const API_BASE = import.meta.env.PROD
  ? 'https://projects.hts.pro/api/sub-onboarding'
  : '/api/sub-onboarding';

import type {
  PaymentTermsDays,
  PublicAgreementStatus,
  SubAgreementPublicState,
  SubmissionAgreementStatus,
} from '@hts/shared';

/**
 * Extended GET /status/:accessToken response (MUL-3919 §3.1). The legacy top
 * level fields are preserved; the new `agreement` block carries the derived
 * public state, the sub-facing `referenceId`, and `canAcceptStandard`. The
 * server derives `state` from `status` + `issuanceStatus`; the UI never
 * recomputes it.
 */
export interface SubOnboardingStatusResponse {
  status: SubmissionAgreementStatus;
  companyName: string;
  submittedAt: string;
  reviewedAt: string | null;
  docusignSentAt: string | null;
  docusignSignedAt: string | null;
  canOpenAgreement: boolean;
  agreement: PublicAgreementStatus | null;
  /** Legacy fields kept for any existing consumers during the deprecation window. */
  termsRejectionReason?: string | null;
  reason?: string | null;
}

/** Body returned by POST /status/:accessToken/accept-standard (§3.1). */
export interface AcceptStandardResponse {
  submissionId: string;
  agreement: {
    state: Extract<SubAgreementPublicState, 'confirming_issue'>;
    referenceId: string;
  };
}

export async function fetchSkills(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/skills`);
  if (!res.ok) throw new Error('Failed to fetch skills');
  return res.json();
}

export async function checkStatus(accessToken: string): Promise<SubOnboardingStatusResponse> {
  const res = await fetch(`${API_BASE}/status/${accessToken}`);
  if (!res.ok) throw new Error('Submission not found');
  return res.json();
}

export async function createSigningSession(accessToken: string): Promise<string> {
  const res = await fetch(`${API_BASE}/status/${accessToken}/signing-session`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unable to open agreement' }));
    throw new Error(err.error || 'Unable to open agreement');
  }
  const data = (await res.json()) as { url: string };
  return data.url;
}

export async function acceptStandardTerms(accessToken: string): Promise<AcceptStandardResponse> {
  const res = await fetch(`${API_BASE}/status/${accessToken}/accept-standard`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unable to continue with standard terms' }));
    throw new Error(err.error || 'Unable to continue with standard terms');
  }
  return res.json();
}

/** Final submission JSON the wizard POSTs (§3.1, §1). */
export interface SubmitJsonPayload {
  requestedTerms?: { paymentTermsDays: PaymentTermsDays };
  /** Required, trimmed 10–1000 chars, when `paymentTermsDays` is 30 | 45 | 60. */
  requestReason?: string;
}

export async function submitOnboarding(formData: FormData): Promise<{
  success: boolean;
  accessToken: string;
}> {
  const res = await fetch(`${API_BASE}/submit`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Submission failed' }));
    if (err.details && Array.isArray(err.details)) {
      const fieldErrors = err.details
        .map((d: { path?: string[]; message?: string }) => {
          const field = d.path?.join('.') ?? 'unknown';
          return `${field}: ${d.message}`;
        })
        .join('\n');
      throw new Error(`Validation failed:\n${fieldErrors}`);
    }
    throw new Error(err.error || 'Submission failed');
  }
  return res.json();
}

export async function sendVerificationCode(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/verify/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send code' }));
    throw new Error(err.error || 'Failed to send verification code');
  }
}

export async function checkVerificationCode(email: string, code: string): Promise<{ verified: boolean; paymentTermsNegotiable: boolean }> {
  const res = await fetch(`${API_BASE}/verify/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }));
    throw new Error(err.error || 'Verification failed');
  }
  // Server returns `{ verified, paymentTermsNegotiable }`. The boolean drives
  // whether the wizard renders the Agreement Terms step (MUL-3919 §6.1).
  // Older servers that return an empty body are treated as "not eligible" —
  // the safest default since the standard path always works.
  const body = (await res.json().catch(() => null)) as
    | { verified?: boolean; paymentTermsNegotiable?: boolean }
    | null;
  return {
    verified: body?.verified === true,
    paymentTermsNegotiable: body?.paymentTermsNegotiable === true,
  };
}

const COI_BASE = import.meta.env.PROD ? 'https://projects.hts.pro/api/coi' : '/api/coi';

export async function resolveCoiLink(token: string): Promise<{ companyName: string }> {
  const res = await fetch(`${COI_BASE}/link/${token}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'This link is no longer valid.' }));
    throw new Error(err.error || 'This link is no longer valid.');
  }
  return res.json();
}

export async function authorizeCoiUpload(token: string, email: string): Promise<void> {
  const res = await fetch(`${COI_BASE}/link/${token}/authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'We could not verify that email.' }));
    throw new Error(err.error || 'We could not verify that email.');
  }
}

export async function submitCoiRenewal(token: string, form: FormData): Promise<{ id: string }> {
  const res = await fetch(`${COI_BASE}/link/${token}/renewal`, { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed. Please try again.' }));
    throw new Error(err.error || 'Upload failed. Please try again.');
  }
  return res.json();
}

export interface CoiAnalysis {
  draftId: string;
  ocrStatus: 'ok' | 'partial' | 'unreadable' | 'failed';
  dates: { gl: string | null; auto: string | null; wc: string | null; umbrella: string | null };
  limits: Record<string, string>;
  limitConfidence: Record<string, number>;
}

export interface OnboardingCoiAnalysis {
  analysisToken: string;
  parserStatus: 'ok' | 'partial' | 'unreadable' | 'failed';
  dates: { gl: string | null; auto: string | null; wc: string | null; umbrella: string | null };
  limits: Record<string, string>;
  limitConfidence: Record<string, number>;
}

/** Phase one of onboarding: store and parse the COI before the form can continue. */
export async function analyzeOnboardingCoi(
  contactEmail: string,
  file: File,
): Promise<OnboardingCoiAnalysis> {
  const form = new FormData();
  form.append('contactEmail', contactEmail);
  form.append('coiFile', file);
  const res = await fetch(`${API_BASE}/coi/analyze`, { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Certificate upload failed.' }));
    throw new Error(err.error || 'Certificate upload failed.');
  }
  return res.json();
}

/** Phase one: hand over the certificate and get back what we could read from it. */
export async function analyzeCoiUpload(token: string, form: FormData): Promise<CoiAnalysis> {
  const res = await fetch(`${COI_BASE}/link/${token}/analyze`, { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed. Please try again.' }));
    throw new Error(err.error || 'Upload failed. Please try again.');
  }
  return res.json();
}

/** Phase two: file the certificate with the values the subcontractor confirmed. */
export async function confirmCoiRenewal(
  token: string,
  payload: {
    email: string;
    draftId: string;
    agentEmail?: string;
    limits: Record<string, string>;
  } & Record<string, unknown>,
): Promise<{ id: string }> {
  const res = await fetch(`${COI_BASE}/link/${token}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Could not file this certificate.' }));
    throw new Error(err.error || 'Could not file this certificate.');
  }
  return res.json();
}
